import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { soundManager } from '../../utils/sounds';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: React.ReactNode;
}

const categories = [
  { id: 'all', label: 'All' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'gameplay', label: 'Gameplay' },
  { id: 'wagering', label: 'Wagering' },
  { id: 'fairness', label: 'Fairness' },
  { id: 'technical', label: 'Technical' },
  { id: 'progression', label: 'Progression' },
];

const faqData: FAQItem[] = [
  // Getting Started
  {
    id: 'what-is-solante',
    category: 'getting-started',
    question: 'What is Solante?',
    answer: (
      <>
        Solante is a roguelike pvp deck-builder card game with real-stakes PvP wagering on the
        Solana blockchain. Combine poker hands with powerful Jokers and consumables to outscore your
        opponent and win the pot. It's inspired by games like Balatro but adds competitive
        multiplayer and cryptocurrency wagering.
      </>
    ),
  },
  {
    id: 'how-to-start',
    category: 'getting-started',
    question: 'How do I start playing?',
    answer: (
      <>
        <ol className="list-decimal list-inside space-y-1">
          <li>Connect your Solana wallet (Phantom, Solflare, etc.)</li>
          <li>
            Go to the <strong>Match</strong> page
          </li>
          <li>Either create a new match with your wager amount, or join an existing open match</li>
          <li>Once both players are in, the match begins automatically</li>
        </ol>
      </>
    ),
  },
  {
    id: 'need-crypto',
    category: 'getting-started',
    question: 'Do I need cryptocurrency to play?',
    answer: (
      <>
        For wagered PvP matches, yes — you need SOL in your wallet to stake. However, you can use{' '}
        <Link to="/practice" className="text-arcade-cyan hover:underline">
          Practice Mode
        </Link>{' '}
        to learn the game without connecting a wallet or risking any funds.
      </>
    ),
  },
  {
    id: 'supported-wallets',
    category: 'getting-started',
    question: 'What wallets are supported?',
    answer: (
      <>
        Solante supports all major Solana wallets including:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Phantom</li>
          <li>Solflare</li>
          <li>Coinbase Wallet</li>
          <li>Ledger (hardware)</li>
          <li>Backpack</li>
          <li>Any Wallet Standard compatible wallet</li>
        </ul>
      </>
    ),
  },
  {
    id: 'tutorial',
    category: 'getting-started',
    question: 'Is there a tutorial?',
    answer: (
      <>
        Yes! Use{' '}
        <Link to="/practice" className="text-arcade-cyan hover:underline">
          Practice Mode
        </Link>{' '}
        to learn the game mechanics without risking any funds. You can also check the{' '}
        <Link to="/info" className="text-arcade-cyan hover:underline">
          Info Page
        </Link>{' '}
        for detailed information about hand combinations, Jokers, and consumables.
      </>
    ),
  },

  // Gameplay
  {
    id: 'how-to-win',
    category: 'gameplay',
    question: 'How do I win a match?',
    answer: (
      <>
        You win by either:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            <strong>Reaching 1,000 points first</strong> — build combos with Jokers to maximize your
            score
          </li>
          <li>
            <strong>Depleting your opponent's hearts</strong> — they lose all their lives before
            reaching the target
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'match-duration',
    category: 'gameplay',
    question: 'How long do matches last?',
    answer: (
      <>
        Matches typically last <strong>3-5 minutes</strong> (~15 turns). Each turn has a 45-second
        time limit, so games stay fast-paced and exciting.
      </>
    ),
  },
  {
    id: 'what-are-hearts',
    category: 'gameplay',
    question: 'What are hearts?',
    answer: (
      <>
        Hearts are your lives. You start with 3-5 hearts depending on the difficulty. If you fail to
        meet a blind's target score, you lose a heart. Lose all hearts and you lose the match. Some
        Jokers and consumables can restore or steal hearts.
      </>
    ),
  },
  {
    id: 'what-is-shop',
    category: 'gameplay',
    question: 'What is the shop?',
    answer: (
      <>
        The shop opens between turns where you can spend <strong>chips</strong> to buy:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            <strong>Jokers</strong> — permanent passive abilities that boost your scoring
          </li>
          <li>
            <strong>Consumables</strong> — one-time use items with powerful effects
          </li>
          <li>
            <strong>Services</strong> — like restoring hearts
          </li>
        </ul>
        You can also reroll the shop for a cost to see different items.
      </>
    ),
  },
  {
    id: 'what-are-chips',
    category: 'gameplay',
    question: 'What are chips?',
    answer: (
      <>
        Chips are the <strong>in-match currency</strong> used to buy items from the shop. They are{' '}
        <em>not</em> real money — you earn chips by playing well and from certain Joker effects.
        Chips reset each match.
      </>
    ),
  },
  {
    id: 'how-tempo-works',
    category: 'gameplay',
    question: 'How does Tempo work?',
    answer: (
      <>
        Playing your hand quickly (under 20 seconds) builds a <strong>Tempo streak</strong>:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Streak 1: +8 bonus points</li>
          <li>Streak 2: +15 bonus points</li>
          <li>Streak 3 (Max): +25 bonus points</li>
        </ul>
        Taking longer than 20 seconds resets your streak. Fast play is rewarded!
      </>
    ),
  },
  {
    id: 'sabotage-cards',
    category: 'gameplay',
    question: 'What are sabotage cards?',
    answer: (
      <>
        Sabotage cards (power cards) let you affect your opponent's next turn. Effects include:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Score penalties</li>
          <li>Suit restrictions</li>
          <li>Heart damage</li>
          <li>Heart stealing</li>
        </ul>
        Use them strategically to disrupt your opponent's combos.
      </>
    ),
  },
  {
    id: 'joker-evolution',
    category: 'gameplay',
    question: 'How do jokers evolve?',
    answer: (
      <>
        Jokers can evolve through three tiers: <strong>Base → Awakened → Ascended</strong>. Each
        Joker has specific conditions to evolve (like "trigger 6 times" or "score 400+ with a
        pair"). Evolved Jokers are significantly stronger. Evolution is{' '}
        <strong>per-match only</strong> — they reset each game.
      </>
    ),
  },

  // Wagering & Money
  {
    id: 'how-wagers-work',
    category: 'wagering',
    question: 'How do wagers work?',
    answer: (
      <>
        Both players stake an equal amount of SOL to create the pot. The winner receives{' '}
        <strong>97% of the total pot</strong>. For example, if both players wager 0.5 SOL:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Total pot: 1 SOL</li>
          <li>Winner receives: 0.97 SOL</li>
          <li>House fee: 0.03 SOL (3%)</li>
        </ul>
      </>
    ),
  },
  {
    id: 'supported-currencies',
    category: 'wagering',
    question: 'What currencies can I wager?',
    answer: (
      <>
        Currently, only <strong>SOL</strong> is supported for wagering. USDC support is planned for
        the future.
      </>
    ),
  },
  {
    id: 'house-fee',
    category: 'wagering',
    question: 'What is the house fee?',
    answer: (
      <>
        The house fee is <strong>3%</strong> of the total pot. This goes to the treasury to support
        game development, server costs, and prize pools for tournaments.
      </>
    ),
  },
  {
    id: 'funds-safe',
    category: 'wagering',
    question: 'Are my funds safe?',
    answer: (
      <>
        Yes. Your wager is held in an <strong>on-chain escrow</strong> (Program Derived Address)
        controlled by the smart contract — not by any individual. Funds can only be released through
        the settlement process when the match ends. Neither players nor operators can access the
        escrow directly.
      </>
    ),
  },
  {
    id: 'when-paid',
    category: 'wagering',
    question: 'When do I get paid?',
    answer: (
      <>
        Settlement happens <strong>automatically</strong> when the match ends. The winner's payout
        is transferred directly to their wallet via an on-chain transaction. You'll see your balance
        update within seconds of the match concluding.
      </>
    ),
  },
  {
    id: 'cancel-match',
    category: 'wagering',
    question: 'Can I cancel a match?',
    answer: (
      <>
        You can only cancel a match <strong>before an opponent joins</strong>. Once both players
        have joined and the match starts, it must be played to completion (or forfeited). If you
        forfeit, your opponent wins the pot.
      </>
    ),
  },

  // Fairness & Security
  {
    id: 'is-fair',
    category: 'fairness',
    question: 'Is the game fair?',
    answer: (
      <>
        Yes! Solante uses a <strong>provably fair</strong> system. The deck order is determined by
        combining seeds from both the server and players. The server commits to its seed hash before
        the match starts, making it impossible to manipulate the outcome after seeing player
        actions.
      </>
    ),
  },
  {
    id: 'provably-fair-how',
    category: 'fairness',
    question: 'How does provably fair work?',
    answer: (
      <>
        <ol className="list-decimal list-inside space-y-1">
          <li>Server generates a secret seed and commits its hash on-chain before the match</li>
          <li>Each player provides their own random seed</li>
          <li>All seeds are combined to generate the deck order</li>
          <li>After the match, all seeds are revealed so anyone can verify the deck was fair</li>
        </ol>
        This ensures neither the server nor players can predict or manipulate the cards.
      </>
    ),
  },
  {
    id: 'server-cheat',
    category: 'fairness',
    question: 'Can the server cheat?',
    answer: (
      <>
        <strong>No.</strong> The deck order is determined by combining the server seed with player
        seeds using a deterministic algorithm. Since the server commits to its seed hash before
        seeing player seeds, it cannot know the final deck order in advance. Any tampering would be
        detectable by verifying the seeds post-match.
      </>
    ),
  },
  {
    id: 'verify-match',
    category: 'fairness',
    question: 'How can I verify a match?',
    answer: (
      <>
        After a match completes, all fairness data (server seed, player seeds, deck order) is
        available. You can use this data to independently regenerate the exact same deck order and
        verify no manipulation occurred.
      </>
    ),
  },

  // Technical & Troubleshooting
  {
    id: 'disconnect',
    category: 'technical',
    question: 'What if I disconnect?',
    answer: (
      <>
        You have a <strong>90-second grace period</strong> to reconnect. Your opponent will see a
        countdown. If you reconnect in time, you can continue playing. If the grace period expires,
        you automatically forfeit and your opponent wins.
      </>
    ),
  },
  {
    id: 'timeout',
    category: 'technical',
    question: 'What happens if I timeout?',
    answer: (
      <>
        Each turn has a 45-second time limit. If you don't play in time:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            You receive a <strong>-20 point penalty</strong>
          </li>
          <li>Multiple consecutive timeouts may result in automatic forfeit</li>
        </ul>
        Stay engaged to avoid penalties!
      </>
    ),
  },
  {
    id: 'cant-join',
    category: 'technical',
    question: "Why can't I join a match?",
    answer: (
      <>
        Common reasons:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            <strong>Wallet not connected</strong> — click the wallet button in the top right
          </li>
          <li>
            <strong>Insufficient balance</strong> — you need enough SOL for the wager + transaction
            fees
          </li>
          <li>
            <strong>Network issues</strong> — check your internet connection
          </li>
          <li>
            <strong>Match already filled</strong> — someone else may have joined first
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'network',
    category: 'technical',
    question: 'What network is this on?',
    answer: (
      <>
        Solante runs on <strong>Solana</strong>. You can switch between:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            <strong>Devnet</strong> — for testing with fake SOL (get free devnet SOL from faucets)
          </li>
          <li>
            <strong>Mainnet</strong> — for real-stakes matches with real SOL
          </li>
        </ul>
        Use the cluster switcher in the navigation bar to change networks.
      </>
    ),
  },
  {
    id: 'report-bug',
    category: 'technical',
    question: 'How do I report a bug?',
    answer: (
      <>
        Please report bugs and issues on our GitHub repository. Include details about what happened,
        what you expected, and any error messages you saw. We appreciate your help improving
        Solante!
      </>
    ),
  },

  // Progression & Rewards
  {
    id: 'elo-system',
    category: 'progression',
    question: 'How does ELO work?',
    answer: (
      <>
        ELO is a rating system that tracks your skill level. Everyone starts at{' '}
        <strong>1000 ELO</strong>. Winning increases your ELO, losing decreases it. The amount
        gained/lost depends on your opponent's rating — beating a higher-rated player gives more
        points.
      </>
    ),
  },
  {
    id: 'rank-tiers',
    category: 'progression',
    question: 'What are the rank tiers?',
    answer: (
      <>
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Bronze</strong>: 0-999 ELO
          </li>
          <li>
            <strong>Silver</strong>: 1000-1199 ELO
          </li>
          <li>
            <strong>Gold</strong>: 1200-1399 ELO
          </li>
          <li>
            <strong>Platinum</strong>: 1400-1599 ELO
          </li>
          <li>
            <strong>Diamond</strong>: 1600-1799 ELO
          </li>
          <li>
            <strong>Champion</strong>: 1800+ ELO
          </li>
        </ul>
        Higher ranks experience ELO decay if inactive for too long.
      </>
    ),
  },
  {
    id: 'daily-missions',
    category: 'progression',
    question: 'What are daily missions?',
    answer: (
      <>
        Each day you get <strong>3 random missions</strong> that award bonus XP when completed.
        Examples include:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Win X matches</li>
          <li>Score 500+ in a single hand</li>
          <li>Reach max tempo streak</li>
          <li>Use sabotage cards</li>
        </ul>
        Missions reset at midnight UTC. Login streaks give bonus XP multipliers!
      </>
    ),
  },
  {
    id: 'keep-evolved-jokers',
    category: 'progression',
    question: 'Do I keep evolved jokers?',
    answer: (
      <>
        <strong>No</strong> — Joker evolution is <strong>per-match only</strong>. When a match ends,
        all Jokers reset to their base form. This keeps each match fresh and ensures everyone starts
        on equal footing.
      </>
    ),
  },
];

const FAQAccordionItem: React.FC<{
  item: FAQItem;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ item, isExpanded, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="arcade-panel rounded-xl overflow-hidden"
    >
      <button
        type="button"
        onClick={() => {
          soundManager.play('click');
          onToggle();
        }}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors"
      >
        <span className="font-display text-sm text-white pr-4">{item.question}</span>
        <motion.span
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-arcade-cyan shrink-0"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 text-sm text-white/70 leading-relaxed border-t border-white/10 pt-3">
              {item.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const FAQPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const matchesSearch =
        searchQuery === '' || item.question.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleToggle = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8 py-8">
      {/* Header */}
      <section className="text-center space-y-2">
        <h1 className="font-display text-3xl font-bold neon-text">FAQ</h1>
        <p className="text-sm text-white/50">Everything you need to know about Solante</p>
      </section>

      {/* Search Bar */}
      <div className="max-w-md mx-auto">
        <div className="relative">
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-white/20 bg-black/40 px-4 py-2 pl-10 text-sm text-white placeholder-white/40 focus:border-arcade-cyan/50 focus:outline-none focus:ring-1 focus:ring-arcade-cyan/30"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2">
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => {
              soundManager.play('select');
              setActiveCategory(cat.id);
              setExpandedId(null);
            }}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-wide transition-all ${
              activeCategory === cat.id
                ? 'border-arcade-cyan bg-arcade-cyan/20 text-arcade-cyan'
                : 'border-white/20 bg-white/5 text-white/60 hover:border-white/40 hover:text-white/80'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="space-y-3 max-w-2xl mx-auto">
        {filteredFAQs.length === 0 ? (
          <div className="text-center py-8 text-white/40">
            No questions found matching your search.
          </div>
        ) : (
          filteredFAQs.map((item) => (
            <FAQAccordionItem
              key={item.id}
              item={item}
              isExpanded={expandedId === item.id}
              onToggle={() => handleToggle(item.id)}
            />
          ))
        )}
      </div>

      {/* Footer CTA */}
      <section className="text-center space-y-4 pt-8 border-t border-white/10">
        <p className="text-white/60 text-sm">Still have questions?</p>
        <div className="flex justify-center gap-4">
          <Link
            to="/info"
            onClick={() => soundManager.play('select')}
            className="rounded-lg border border-arcade-cyan/40 bg-arcade-cyan/10 px-6 py-2 text-sm font-semibold text-arcade-cyan hover:bg-arcade-cyan/20 transition-colors"
          >
            View Game Info
          </Link>
          <Link
            to="/practice"
            onClick={() => soundManager.play('select')}
            className="rounded-lg border border-white/20 bg-white/5 px-6 py-2 text-sm font-semibold text-white/80 hover:bg-white/10 transition-colors"
          >
            Try Practice Mode
          </Link>
        </div>
      </section>
    </motion.div>
  );
};
